import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, PartyPopper, RotateCcw, Sparkles } from "lucide-react";
import GameHeader from "@/components/study/GameHeader";
import confetti from "canvas-confetti";
import { Card } from "@/types/linguadeck";
import { useSoundStore } from "@/stores/useSoundStore";
import { resumeAudioContext } from "@/lib/studySfx";
import { playAudio } from "@/lib/audioUtils";

interface GridItem {
  id: string;
  cardId: string;
  text: string;
  type: "front" | "back";
  lang: string;
  /** Pre-generated Neural TTS URL when available */
  audioUrl?: string | null;
}

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const MAX_PAIRS_PER_LEVEL = 4;

function buildInitialGrid(cards: Card[]): GridItem[] {
  const valid = cards.filter((c) => (c.front || "").trim() && (c.back || "").trim());
  const picked = shuffleArray(valid).slice(0, MAX_PAIRS_PER_LEVEL);
  const pairs: GridItem[] = [];
  let idCounter = 0;
  picked.forEach((card, idx) => {
    const cardId = card.id ?? `card-${idx}`;
    const frontLang = card.frontLang ?? "en";
    const backLang = card.backLang ?? "en";
    pairs.push({
      id: `item-${idCounter++}`,
      cardId,
      text: (card.front || "").trim(),
      type: "front",
      lang: frontLang,
      audioUrl: card.frontAudioUrl ?? null,
    });
    pairs.push({
      id: `item-${idCounter++}`,
      cardId,
      text: (card.back || "").trim(),
      type: "back",
      lang: backLang,
      audioUrl: card.backAudioUrl ?? null,
    });
  });
  return shuffleArray(pairs);
}

const GAMEPLAY_MUSIC_URL =
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3";
const VICTORY_MUSIC_URL =
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3";


interface MatchModeProps {
  cards: Card[];
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
  onFavorite?: () => void;
  isFavorite?: boolean;
}

export default function MatchMode({ cards, onBack, onProgressComplete, onFavorite, isFavorite = false }: MatchModeProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    if (onBack) onBack();
    else navigate(-1);
  }, [onBack, navigate]);
  const [gridItems, setGridItems] = useState<GridItem[]>(() => buildInitialGrid(cards));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [matchedCardIds, setMatchedCardIds] = useState<Set<string>>(new Set());
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  const [showMismatch, setShowMismatch] = useState(false);

  const { isSoundEnabled, isVoiceEnabled, voiceVolume, gameVolume } = useSoundStore();

  const gameplayAudioRef = useRef<HTMLAudioElement | null>(null);
  const victoryAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioUnlockedRef = useRef(false);

  const totalPairs = useMemo(() => gridItems.length / 2, [gridItems.length]);
  const totalDeckCards = useMemo(
    () => cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()).length,
    [cards]
  );

  // Create both audio elements on mount, cleanup on unmount; ignore load errors so missing assets don't break the app
  useEffect(() => {
    let gameplayAudio: HTMLAudioElement | null = null;
    let victoryAudio: HTMLAudioElement | null = null;
    try {
      gameplayAudio = new Audio(GAMEPLAY_MUSIC_URL);
      victoryAudio = new Audio(VICTORY_MUSIC_URL);
      gameplayAudio.onerror = () => { /* ignore */ };
      victoryAudio.onerror = () => { /* ignore */ };
      gameplayAudioRef.current = gameplayAudio;
      victoryAudioRef.current = victoryAudio;
    } catch {
      gameplayAudioRef.current = null;
      victoryAudioRef.current = null;
    }
    return () => {
      try {
        if (gameplayAudio) {
          gameplayAudio.pause();
          gameplayAudio.src = "";
        }
        if (victoryAudio) {
          victoryAudio.pause();
          victoryAudio.src = "";
        }
      } finally {
        gameplayAudioRef.current = null;
        victoryAudioRef.current = null;
      }
    };
  }, []);

  // Sync audio element volume with global store
  useEffect(() => {
    const gameplay = gameplayAudioRef.current;
    const victory = victoryAudioRef.current;
    if (gameplay) gameplay.volume = gameVolume;
    if (victory) victory.volume = gameVolume;
  }, [gameVolume]);

  // Final progress on game complete (in case granular updates missed the last one)
  useEffect(() => {
    if (isFinished && totalDeckCards > 0 && onProgressComplete) {
      const cardsCorrected = totalPairs * 2;
      const pct = Math.min(100, Math.round((cardsCorrected / totalDeckCards) * 100));
      onProgressComplete(pct);
    }
  }, [isFinished, totalPairs, totalDeckCards, onProgressComplete]);

  // Victory transition: forcefully stop gameplay and play victory when game ends
  useEffect(() => {
    if (isFinished && isSoundEnabled) {
      if (gameplayAudioRef.current) {
        gameplayAudioRef.current.pause();
      }
      if (victoryAudioRef.current) {
        victoryAudioRef.current.currentTime = 0;
        victoryAudioRef.current.play().catch((e: unknown) => {
          if (e && typeof e === "object" && "name" in e && (e as { name: string }).name === "NotAllowedError") return;
          console.error("Victory play failed:", e);
        });
      }
    }
  }, [isFinished, isSoundEnabled]);

  // Dual-track playback: gameplay vs victory, respecting global sound (excludes victory play – handled above)
  useEffect(() => {
    const gameplay = gameplayAudioRef.current;
    const victory = victoryAudioRef.current;
    if (!gameplay || !victory) return;

    if (!isSoundEnabled) {
      gameplay.pause();
      gameplay.currentTime = 0;
      victory.pause();
      victory.currentTime = 0;
      return;
    }

    if (isFinished) {
      gameplay.pause();
      gameplay.currentTime = 0;
      victory.currentTime = 0;
      return;
    }

    if (isPlaying) {
      victory.pause();
      victory.currentTime = 0;
      gameplay.currentTime = 0;
      gameplay.loop = true;
      gameplay.play().catch((err: unknown) => {
        if (err && typeof err === "object" && "name" in err && (err as { name: string }).name === "NotAllowedError") return;
        console.warn("Browser autoplay prevented gameplay playback", err);
      });
    } else {
      gameplay.pause();
      gameplay.currentTime = 0;
      victory.pause();
      victory.currentTime = 0;
    }
  }, [isPlaying, isFinished, isSoundEnabled]);

  const getCongratulations = useCallback(() => {
    return [
      t("matchMode.compliment1"),
      t("matchMode.compliment2"),
      t("matchMode.compliment3"),
      t("matchMode.compliment4"),
      t("matchMode.compliment5"),
      t("matchMode.compliment6"),
    ];
  }, [t]);

  const randomCongratulation = useMemo(() => {
    if (!isFinished) return "";
    const congratulations = getCongratulations();
    return congratulations[Math.floor(Math.random() * congratulations.length)];
  }, [isFinished, getCongratulations]);

  // Timer: increment every 100ms while playing
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => setTime((prev) => prev + 1), 100);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const handlePlayAgain = useCallback(() => {
    const newGrid = buildInitialGrid(cards);
    setGridItems(newGrid);
    setSelectedIds(new Set());
    setMatchedCardIds(new Set());
    setTime(0);
    setIsPlaying(true);
    setIsFinished(false);
    setShowMismatch(false);
  }, [cards]);

  const handleClick = useCallback(
    (id: string) => {
      if (!audioUnlockedRef.current) {
        audioUnlockedRef.current = true;
        resumeAudioContext().catch(() => {});
        const gameplay = gameplayAudioRef.current;
        if (gameplay && isSoundEnabled) {
          gameplay.volume = gameVolume;
          gameplay.loop = true;
          gameplay.currentTime = 0;
          gameplay.play().catch(() => {});
        }
      }

      const item = gridItems.find((x) => x.id === id);
      if (isFinished || matchedCardIds.size === totalPairs) return;
      if (!item || matchedCardIds.has(item.cardId)) return;
      if (selectedIds.has(id)) return;
      if (showMismatch) return;

      const newSelected = new Set(selectedIds);
      newSelected.add(id);

      if (newSelected.size === 1) {
        setSelectedIds(newSelected);
        return;
      }

      if (newSelected.size === 2) {
        const [aId, bId] = Array.from(newSelected);
        const a = gridItems.find((x) => x.id === aId)!;
        const b = gridItems.find((x) => x.id === bId)!;
        const isMatch = a.cardId === b.cardId && a.type !== b.type;

        if (isMatch) {
          const newMatched = new Set(matchedCardIds).add(a.cardId);
          setMatchedCardIds(newMatched);
          setSelectedIds(new Set());
          // TTS: speak the target/learning word (front = term being learned, e.g. "apple"; back = native translation, e.g. "яблоко")
          if (isVoiceEnabled) {
            const targetItem = a.type === "front" ? a : b;
            const card = cards.find((c) => (c.id ?? "").toString() === targetItem.cardId);
            if (card && (targetItem.text || "").trim()) {
              void playAudio({
                card,
                side: targetItem.type,
                deckLang: targetItem.lang,
                volume: voiceVolume ?? 1,
              });
            }
          }
          if (totalDeckCards > 0 && onProgressComplete) {
            const cardsCorrected = newMatched.size * 2;
            const pct = Math.min(100, Math.round((cardsCorrected / totalDeckCards) * 100));
            onProgressComplete(pct);
          }
          return;
        }

        setShowMismatch(true);
        setTimeout(() => {
          setSelectedIds(new Set());
          setShowMismatch(false);
        }, 400);
      }
    },
    [gridItems, selectedIds, matchedCardIds, totalPairs, totalDeckCards, isFinished, showMismatch, isSoundEnabled, isVoiceEnabled, voiceVolume, gameVolume, onProgressComplete, cards]
  );

  useEffect(() => {
    if (matchedCardIds.size === totalPairs && totalPairs > 0) {
      setIsPlaying(false);
      setIsFinished(true);
    }
  }, [matchedCardIds.size, totalPairs]);

  // Advanced multi-stage fireworks celebration when finished
  useEffect(() => {
    if (!isFinished) return;

    const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

    const fire = (particleRatio: number, opts: confetti.Options = {}) => {
      confetti({
        ...opts,
        colors,
        origin: opts.origin ?? { y: 0.7 },
        particleCount: Math.floor(200 * particleRatio),
        shapes: ["circle", "square"],
        scalar: opts.scalar ?? 1.2,
        drift: opts.drift ?? 0.5,
        gravity: opts.gravity ?? 1.1,
      });
    };

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });

    const t = setTimeout(() => {
      fire(0.3, { spread: 80, origin: { x: 0.3, y: 0.6 } });
      fire(0.3, { spread: 80, origin: { x: 0.7, y: 0.6 } });
    }, 400);

    return () => clearTimeout(t);
  }, [isFinished]);

  const getCardClassName = (item: GridItem) => {
    const base =
      "min-h-[110px] rounded-xl shadow-sm flex items-center justify-center p-4 text-center cursor-pointer transition-all duration-200 font-medium text-base bg-white dark:bg-[hsl(var(--card-bg))] border-2";
    if (matchedCardIds.has(item.cardId)) {
      return `${base} border-transparent opacity-0 pointer-events-none`;
    }
    const isSelected = selectedIds.has(item.id);
    const isMismatch = showMismatch && isSelected;
    if (isMismatch) {
      return `${base} border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 animate-shake`;
    }
    if (isSelected) {
      return `${base} border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:shadow-md`;
    }
    return `${base} border-gray-200 dark:border-[hsl(var(--border))] text-gray-800 dark:text-[hsl(var(--foreground))] hover:shadow-md`;
  };

  if (gridItems.length === 0) {
    return (
      <div className="h-full w-full flex flex-col overflow-hidden">
        <header className="shrink-0 px-4 pt-4 flex flex-col items-center">
          <GameHeader onBack={handleBack} />
        </header>
        <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
          <p className="text-gray-500 dark:text-gray-400">{t("learnMode.noCards")}</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{t("learnMode.noCardsHint")}</p>
        </div>
      </div>
    );
  }

  if (isFinished) {
    return (
      <div className="h-full w-full flex flex-col overflow-hidden">
        <header className="shrink-0 px-4 pt-4 flex flex-col items-center">
          <GameHeader onBack={handleBack} />
        </header>
        <div className="flex-1 min-h-0 overflow-hidden pb-24">
          <div className="flex flex-col items-center w-full max-w-5xl mx-auto min-h-[400px] py-12 px-4">
            {/* Top section: heading + illustration */}
            <div className="flex flex-col md:flex-row justify-between items-center md:items-start w-full mb-10 gap-8">
              {/* Left column: text */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-gray-100 leading-tight mb-4">
                  {t("matchMode.congratulations")}
                  <br />
                  {t("matchMode.youDidGreat")}
                </h1>
                {randomCongratulation && (
                  <p className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
                    {randomCongratulation}
                  </p>
                )}
              </div>
              {/* Right column: illustration */}
              <div className="relative shrink-0">
                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-amber-400/80 blur-sm" aria-hidden />
                <div className="absolute -bottom-1 -left-1 w-8 h-8 rounded-full bg-blue-400/60 blur-sm" aria-hidden />
                <Sparkles
                  className="absolute -top-3 right-4 w-6 h-6 text-amber-400 animate-pulse"
                  aria-hidden
                />
                <Sparkles
                  className="absolute bottom-2 -left-2 w-5 h-5 text-blue-400 animate-pulse"
                  aria-hidden
                />
                <PartyPopper
                  className="w-32 h-32 md:w-40 md:h-40 text-purple-500 dark:text-purple-400 animate-bounce relative z-10"
                  aria-hidden
                />
              </div>
            </div>

          {/* Center/bottom section: checkmark, time, button */}
          <div className="flex flex-col items-center w-full">
            <div className="relative flex items-center justify-center mb-6">
              <div
                className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full animate-pulse scale-150 z-0"
                aria-hidden
              />
              <CheckCircle2
                className="w-24 h-24 text-emerald-600 relative z-10 animate-in zoom-in-50 spin-in-12 duration-700 drop-shadow-lg"
                aria-hidden
              />
            </div>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              {t("matchMode.yourTime", { time: (time / 10).toFixed(1) })}
            </p>
            <button
              type="button"
              onClick={handlePlayAgain}
              className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
            >
              <RotateCcw className="w-5 h-5 shrink-0" />
              {t("shared.playAgain")}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Header: shrink-0 so it stays at top and never clips */}
      <header className="shrink-0 w-full max-w-4xl mx-auto px-4 pt-4 flex flex-col items-center">
        <GameHeader onBack={handleBack} />
        <div className="flex justify-center mt-4">
          <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 tabular-nums">
            {(time / 10).toFixed(1)}s
          </span>
        </div>
      </header>

      {/* Grid: flex-1 with balanced top/bottom spacing, no scroll */}
      <div className="flex-1 min-h-0 overflow-hidden px-4 pb-24 flex flex-col justify-center">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-8 w-full max-w-4xl mx-auto">
          {gridItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleClick(item.id)}
              className={getCardClassName(item)}
            >
              <span className="line-clamp-3 break-words">{item.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
